import React, { FC, useCallback, useState } from 'react';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  UncontrolledTooltip,
} from 'reactstrap';
import { StripeConnectConfiguration } from '../../../../shared/payment_gateway/stripeconnect';
import { MaterialIcon } from '../../../components';
import { useTranslation } from '../../../translations';
import { Fetcher, useConfig, useFetcher } from '../../../utils';
import { ConfigTextBoxSetting } from './AbstractLiveSettings';

const PaymentGatewaySettingsHeader: FC<{
  readonly loading: boolean;
  readonly refresh: () => void;
}> = ({ loading, refresh }) => {
  const { ts } = useTranslation();
  return (
    <>
      <h2>{ts('payment_gateways')}</h2>
      <Button className="float-end" color="success" disabled={loading} onClick={refresh}>
        {ts('common.refresh')}
      </Button>
      <p className="custom-control">{ts('payments.manual.info')}</p>
      <hr />
    </>
  );
};

export const PaymentGatewaySettings: FC = () => {
  const result = useFetcher(async () => {
    return await api.getPaymentGatewayConfigs();
  }, []);

  const { ts } = useTranslation();

  if (!result.success) {
    return (
      <>
        <PaymentGatewaySettingsHeader loading={result.loading} refresh={result.refresh} />
        <Fetcher result={result} />
      </>
    );
  }

  return (
    <>
      <PaymentGatewaySettingsHeader loading={result.loading} refresh={result.refresh} />
      <h3>
        {ts('stripe_connect')}{' '}
        <a
          href="https://docs.stripe.com/connect"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          <MaterialIcon name="help" />
        </a>
      </h3>
      <StripeConnectComponent account={result.data.stripeConnect} />
      <hr />
      <h3>
        {ts('payPagSeguro.pagSeguroCheckout')}{' '}
        <a
          href="https://developer.pagbank.com.br/reference/criar-checkout"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          <MaterialIcon name="help" />
        </a>
      </h3>
      {!result.data.pagSeguro && <PagSeguroSettingsForm refresh={result.refresh} />}
      {result.data.pagSeguro && (
        <>
          <dl>
            <dt>{ts('base')}</dt>
            <dd>{result.data.pagSeguro.base}</dd>
            <dt>{ts('common.token')}</dt>
            <dd>{result.data.pagSeguro.token}</dd>
          </dl>
          <Button
            color="danger"
            onClick={async () => {
              await api.unsetPagSeguroCheckoutConfig();
              result.refresh();
            }}
            outline
          >
            {ts('remove_credentials')}
          </Button>
        </>
      )}
    </>
  );
};

const StripeConnectComponent: FC<{
  readonly account?: StripeConnectConfiguration;
}> = ({ account }) => {
  const { ts } = useTranslation();

  return (
    <>
      <StripeConnectButton firstTime={!account} />
      {!account && <div>{ts('stripe.connect.disconnected.notice')}</div>}
      {account && (
        <>
          <StripeStatusBadges account={account} />
          <p>{ts('stripe.connect.connected.notice', account.accountId)}</p>
          <p>
            {ts(
              'stripe.connect.connected.scope',
              ts(
                account.conventionSpecific
                  ? 'stripe.connect.connected.scope.con'
                  : 'stripe.connect.connected.scope.org',
              ),
            )}
          </p>
        </>
      )}
    </>
  );
};

const StripeConnectButton: FC<{
  readonly firstTime?: boolean;
}> = ({ firstTime }) => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { ts } = useTranslation();
  return (
    <>
      {showModal && (
        <StripeModal
          close={() => {
            setShowModal(false);
          }}
        />
      )}
      {showConfirmationModal && (
        <Modal isOpen>
          <ModalHeader>{ts('stripe.connect.confirmation_modal.title')}</ModalHeader>
          <ModalBody>{ts('stripe.connect.confirmation_modal.description')}</ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                setShowModal(true);
                setShowConfirmationModal(false);
              }}
            >
              {ts('stripe.connect.confirmation_modal.confirm')}
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setShowConfirmationModal(false);
              }}
            >
              {ts('common.cancel')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
      <Button
        className="float-end"
        color="primary"
        onClick={() => {
          if (firstTime) {
            setShowConfirmationModal(true);
          } else {
            setShowModal(true);
          }
        }}
        style={{ display: 'block' }}
      >
        {ts(firstTime ? 'stripe.connect.action.first' : 'stripe.connect.action')}
      </Button>
    </>
  );
};

const StripeStatusBadges: FC<{
  readonly account: StripeConnectConfiguration;
}> = ({ account }) => {
  const { ts, t } = useTranslation();

  const { chargesEnabled, detailsSubmitted } = account;

  return (
    <div className="badge-list compact float-end">
      <Badge color={chargesEnabled ? 'success' : 'danger'} id="stripeStatusBadge">
        {ts(
          'stripe.connect.status',
          ts(chargesEnabled ? 'stripe.connect.status.connected' : 'stripe.connect.status.disabled'),
        )}
      </Badge>
      {!chargesEnabled && (
        <UncontrolledTooltip placement="right" target="stripeStatusBadge">
          {ts('stripe.connect.status.disabled.tooltip')}
        </UncontrolledTooltip>
      )}
      {!detailsSubmitted && (
        <>
          <Badge color="danger" id="stripeRequirementsStatusBadge">
            {ts('stripe.connect.status.details_required.label')}
          </Badge>
          <UncontrolledTooltip placement="bottom" target="stripeRequirementsStatusBadge">
            {t(
              'stripe.connect.status.details_required.tooltip',
              <strong>{ts('stripe.connect.action')}</strong>,
            )}
          </UncontrolledTooltip>
        </>
      )}
    </div>
  );
};

const StripeModal: FC<{
  readonly close: () => void;
}> = ({ close }) => {
  const stripeData = useFetcher(async () => {
    // Call to API to retrieve a link for this session
    // and return the link, which will be onClickable.
    return await api.createStripeConnectLink(true);
  });

  const { organization } = useConfig();

  const { ts } = useTranslation();

  return (
    <Modal isOpen>
      <ModalHeader>{ts('stripe_connect')}</ModalHeader>
      <ModalBody>
        <p>{ts('stripe.connect.info.explain')}</p>
        <p>{ts('stripe.connect.info.warning')}</p>
        <ul>
          <li>
            {ts('stripe.connect.info.country')}{' '}
            <strong>{organization.country.toUpperCase()}</strong>
          </li>
          <li>
            {ts('stripe.connect.info.currency')}{' '}
            <strong>{organization.currency.toUpperCase()}</strong>
          </li>
        </ul>
      </ModalBody>
      <ModalFooter>
        {!stripeData.data && (
          <Button className="mr-auto" color="primary" disabled>
            <Spinner size="sm" /> {ts('common.loading')}
          </Button>
        )}
        {stripeData.data && (
          <a
            className="btn btn-primary mr-auto"
            href={stripeData.data.link}
            onClick={() => {
              close();
            }}
          >
            {ts('stripe.connect.info.button')}
          </a>
        )}
        <Button color="secondary" onClick={close}>
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const PagSeguroSettingsForm: FC<{ readonly refresh: () => void }> = ({ refresh }) => {
  const [base, setBase] = useState('');
  const [token, setToken] = useState('');

  const [saving, setSaving] = useState(false);

  const onSave = useCallback(async () => {
    setSaving(true);
    await api.setPagSeguroCheckoutConfig({ base, token });

    setSaving(false);
    refresh();
  }, [base, token, refresh]);

  const { ts } = useTranslation();

  return (
    <div>
      <p>
        <strong>{ts('add_token')}</strong>
      </p>
      <p>{ts('pagseguro.explanation')}</p>
      <ConfigTextBoxSetting
        description="pagseguro_hostname_description"
        displayName="common.hostname"
        name="base"
        placeholder="xxxxxxxxx.api.pagseguro.com"
        saving={saving}
        update={(val) => {
          setBase(val?.valueOf() ?? '');
        }}
        value={base}
      />
      <ConfigTextBoxSetting
        description="pagseguro_token_description"
        displayName="common.token"
        name="token"
        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        saving={saving}
        update={(val) => {
          setToken(val?.valueOf() ?? '');
        }}
        value={token}
      />
      <Button className="float-end" color="primary" disabled={saving} onClick={onSave}>
        {ts('common.save')}
      </Button>
    </div>
  );
};
